import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ISearchOptions } from 'api/catalog/models';

import { useQueryString } from './useQueryString';

export type RefinableFacets = Omit<
  ISearchOptions,
  'q' | 'page' | 'size' | 'sort' | 'itemNumbers' | 'upcs'
>;

export type RefinableFacetsKey = keyof RefinableFacets;

export type ItemSearchFacets = Pick<ISearchOptions, 'itemNumbers' | 'upcs'>;

export type ItemSearchFacetsKey = keyof ItemSearchFacets;

export const REFINABLE_FACET_KEYS: RefinableFacetsKey[] = [
  'brandNames',
  'departmentNames',
  'categoryNames',
  'subCategoryNames',
  'dietaryLifestyleAttributes',
  'freeFromAttributes',
  'marketingAttributes',
  'organicCodes',
  'packConfigCodes',
  'sourceItemAttributes',
  'subCategoryIds',
  'hostSystems',
];

export const ITEM_SEARCH_FACET_KEYS: ItemSearchFacetsKey[] = [
  'itemNumbers',
  'upcs',
];

export type SearchParams = RefinableFacets & ItemSearchFacets;

export const getSearchOptionsFromQueryString = (
  searchParams: URLSearchParams
): ItemSearchFacets => {
  const ret: SearchParams = {};
  [...REFINABLE_FACET_KEYS, ...ITEM_SEARCH_FACET_KEYS].forEach((key) => {
    const val = searchParams.get(key);
    ret[key] = val ? val.split(',') : [];
  });
  return ret;
};

export const getRefinableFacetsFromQueryString = (
  searchParams: URLSearchParams
): RefinableFacets => {
  const ret: RefinableFacets = {};
  REFINABLE_FACET_KEYS.forEach((key) => {
    const val = searchParams.get(key);
    ret[key] = val ? val.split(',') : [];
  });
  return ret;
};

const getGenericSelectedCount = (
  key: RefinableFacetsKey,
  facets: RefinableFacets
) => {
  const val = facets[key];
  return val ? val.length : 0;
};

export const useFacets = () => {
  const [searchParams] = useSearchParams();
  const { updateQueryString, isQueryStringApplied, getQueryStringArray } =
    useQueryString();

  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [upcs, setUpcs] = useState(getQueryStringArray('upcs'));
  const [itemNumbers, setItemNumbers] = useState(
    getQueryStringArray('itemNumbers')
  );
  const [refinableFacets, setRefinableFacets] = useState<RefinableFacets>(
    getRefinableFacetsFromQueryString(searchParams)
  );

  const getCurrentlyAppliedCount = useCallback(
    (queryStringKey: RefinableFacetsKey) =>
      getGenericSelectedCount(
        queryStringKey,
        getRefinableFacetsFromQueryString(searchParams)
      ),
    [searchParams]
  );

  const isSearchApplied = useCallback(() => {
    return isQueryStringApplied('q');
  }, [isQueryStringApplied]);

  const getSelectedCount = useCallback(
    (queryStringKey: RefinableFacetsKey) =>
      getGenericSelectedCount(queryStringKey, refinableFacets),
    [refinableFacets]
  );

  const isFacetSelected = useCallback(
    (queryStringKey: RefinableFacetsKey, facetCode: string) => {
      const val = refinableFacets[queryStringKey];
      return val ? val.includes(facetCode) : false;
    },
    [refinableFacets]
  );

  const applyFacets = useCallback(() => {
    updateQueryString({
      ...refinableFacets,
      q: searchTerm,
      upcs,
      itemNumbers,
      page: 0,
    });
  }, [itemNumbers, refinableFacets, searchTerm, upcs, updateQueryString]);

  const handleSelectionChange = useCallback(
    (queryStringKey: RefinableFacetsKey, facetCodes: string[] = []) => {
      setRefinableFacets((prev) => {
        const selectedValues = prev[queryStringKey];
        if (selectedValues) {
          const removed: string[] = [];
          const added: string[] = [];
          facetCodes.forEach((facet) => {
            if (selectedValues.includes(facet)) {
              removed.push(facet);
            } else {
              added.push(facet);
            }
          });
          return {
            ...prev,
            [queryStringKey]: [
              ...selectedValues.filter(
                (val) => !removed.map((codeFacet) => codeFacet).includes(val)
              ),
              ...added.map((codeFacet) => codeFacet),
            ],
          };
        } else {
          return {
            ...prev,
            [queryStringKey]: facetCodes,
          };
        }
      });
    },
    []
  );

  const handleSelectAllChange = useCallback(
    (queryStringKey: RefinableFacetsKey, allFacetCodes: string[] = []) => {
      const selectedCount = getSelectedCount(queryStringKey);
      if (selectedCount < allFacetCodes.length) {
        setRefinableFacets((prev) => ({
          ...prev,
          [queryStringKey]: allFacetCodes,
        }));
      } else {
        setRefinableFacets((prev) => ({
          ...prev,
          [queryStringKey]: undefined,
        }));
      }
    },
    [getSelectedCount]
  );

  const handleReset = useCallback(() => {
    setRefinableFacets(
      getRefinableFacetsFromQueryString(new URLSearchParams())
    );
    setSearchTerm('');
    setUpcs([]);
    setItemNumbers([]);
  }, []);

  const handleSearchChange = useCallback((val = '') => {
    setSearchTerm(val);
  }, []);

  const itemSearchTypeApplied = useMemo(() => {
    const keysToCheck: ItemSearchFacetsKey[] = ['upcs', 'itemNumbers'];
    let ret: ItemSearchFacetsKey | undefined = undefined;
    for (let i = 0, found = false; i < keysToCheck.length && !found; i++) {
      found = Boolean(isQueryStringApplied(keysToCheck[i]));
      if (found) {
        ret = keysToCheck[i];
      }
    }

    return ret;
  }, [isQueryStringApplied]);

  const [itemSearchType, setItemSearchType] = useState<ItemSearchFacetsKey>(
    itemSearchTypeApplied || 'itemNumbers'
  );

  const handleItemSearchChange = useCallback(
    (type: ItemSearchFacetsKey, values: string[] = []) => {
      if (type === 'upcs') {
        setItemSearchType('upcs');
        setItemNumbers([]);
        setUpcs(values);
      } else if (type === 'itemNumbers') {
        setItemSearchType('itemNumbers');
        setUpcs([]);
        setItemNumbers(values);
      } else {
        // do nothing
      }
    },
    []
  );

  const itemSearchValues = useMemo(() => {
    if (itemSearchType === 'itemNumbers') {
      return itemNumbers;
    } else if (itemSearchType === 'upcs') {
      return upcs;
    } else {
      return [];
    }
  }, [itemNumbers, itemSearchType, upcs]);

  const isItemSearchApplied = useCallback(() => {
    return itemSearchTypeApplied !== undefined;
  }, [itemSearchTypeApplied]);

  return {
    refinableFacets,
    searchTerm,
    itemSearchTypeApplied,
    itemSearchType,
    itemSearchValues,
    getSelectedCount,
    getCurrentlyAppliedCount,
    isFacetSelected,
    isSearchApplied,
    isItemSearchApplied,
    applyFacets,
    handleSelectionChange,
    handleSelectAllChange,
    handleReset,
    handleSearchChange,
    handleItemSearchChange,
  };
};
